import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ErrorMessage {
  message: string;
  severity: AlertColor;
  timestamp: number;
}

/**
 * GlobalErrorHandler - Component that listens for custom error events
 * and displays them as snackbar notifications.
 * 
 * Events handled:
 * - auth-error: Authentication/token expiration errors
 * - app-error: General application errors
 */
export const GlobalErrorHandler: React.FC = () => {
  const [error, setError] = useState<ErrorMessage | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Handler for auth errors (token expiration, etc.)
    const handleAuthError = (event: CustomEvent) => {
      const { message, severity = 'warning' } = event.detail;
      setError({
        message,
        severity,
        timestamp: Date.now(),
      });
      setOpen(true);
    };

    // Handler for general app errors
    const handleAppError = (event: CustomEvent) => {
      const { message, severity = 'error' } = event.detail;
      setError({
        message,
        severity,
        timestamp: Date.now(),
      });
      setOpen(true);
    };

    // Add event listeners
    window.addEventListener('auth-error', handleAuthError as EventListener);
    window.addEventListener('app-error', handleAppError as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('auth-error', handleAuthError as EventListener);
      window.removeEventListener('app-error', handleAppError as EventListener);
    };
  }, []);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={error?.severity === 'error' ? 6000 : 4000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleClose} 
        severity={error?.severity || 'error'} 
        variant="filled"
        sx={{ width: '100%' }}
      >
        {error?.message}
      </Alert>
    </Snackbar>
  );
};

export default GlobalErrorHandler;